/* FARS theme — global behaviors
 * - Mobile nav toggle
 * - Fade-in on scroll (IntersectionObserver)
 * - Announcement-bar item expand (mobile)
 * - FAQ accordion
 * - Cookie consent banner
 * - Quantity stepper
 */

(function () {
  "use strict";

  /* -------------------------------------------------------------
   * Mobile nav toggle
   * ----------------------------------------------------------- */
  const header = document.querySelector(".site-header");
  if (header) {
    const toggle = header.querySelector(".site-header__menu-toggle");
    if (toggle) {
      toggle.addEventListener("click", () => {
        const isOpen = header.classList.toggle("is-menu-open");
        toggle.setAttribute("aria-expanded", String(isOpen));
      });
    }
  }

  /* -------------------------------------------------------------
   * Fade-in on scroll
   * Any element with .fade-in becomes .is-visible when it enters
   * the viewport. Uses one shared observer.
   * ----------------------------------------------------------- */
  const fadeEls = document.querySelectorAll(".fade-in");
  if (fadeEls.length > 0) {
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              obs.unobserve(entry.target);
            }
          });
        },
        // threshold: 0 — a fixed ratio (e.g. 0.1) can never be reached by
        // elements taller than the viewport (long articles), leaving them
        // permanently invisible.
        { threshold: 0, rootMargin: "0px 0px -40px 0px" }
      );
      fadeEls.forEach((el) => io.observe(el));
    } else {
      fadeEls.forEach((el) => el.classList.add("is-visible"));
    }
  }

  /* -------------------------------------------------------------
   * Announcement-bar item expand (mobile)
   * Tap the title to reveal the body (only when body exists and
   * we're on a narrow viewport).
   * ----------------------------------------------------------- */
  document.querySelectorAll("[data-announcement-toggle]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      const body = btn.querySelector(".announcement-bar__body--mobile");
      if (!body) return;
      const visible = body.style.display === "block";
      body.style.display = visible ? "none" : "block";
      btn.setAttribute("aria-expanded", String(!visible));
    });
  });

  /* -------------------------------------------------------------
   * FAQ accordion
   * ----------------------------------------------------------- */
  document.querySelectorAll("[data-faq-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const item = btn.closest("[data-faq-item]");
      if (!item) return;
      const isOpen = item.classList.toggle("is-open");
      btn.setAttribute("aria-expanded", String(isOpen));
    });
  });

  /* -------------------------------------------------------------
   * Cookie consent banner
   * Persists a flag in localStorage. The snippet renders the
   * banner hidden; we show it if no flag is set.
   * ----------------------------------------------------------- */
  const cookieBanner = document.querySelector("[data-cookie-banner]");
  if (cookieBanner) {
    const STORAGE_KEY = "fars-cookie-consent";
    const stored = (function () {
      try { return localStorage.getItem(STORAGE_KEY); } catch (_) { return null; }
    })();

    if (!stored) {
      cookieBanner.classList.add("is-visible");
    }

    cookieBanner.querySelectorAll("[data-cookie-accept]").forEach((btn) => {
      btn.addEventListener("click", () => {
        try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch (_) {}
        cookieBanner.classList.remove("is-visible");
      });
    });
    cookieBanner.querySelectorAll("[data-cookie-decline]").forEach((btn) => {
      btn.addEventListener("click", () => {
        try { localStorage.setItem(STORAGE_KEY, "declined"); } catch (_) {}
        cookieBanner.classList.remove("is-visible");
      });
    });
  }

  /* -------------------------------------------------------------
   * Quantity steppers
   * Buttons with data-qty-change="1" or "-1" bump the sibling
   * input with data-qty-input. Respects input min/max.
   * ----------------------------------------------------------- */
  document.querySelectorAll("[data-qty-change]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const delta = parseInt(btn.getAttribute("data-qty-change"), 10) || 0;
      const wrapper = btn.closest("[data-qty-wrapper]");
      if (!wrapper) return;
      const input = wrapper.querySelector("[data-qty-input]");
      if (!input) return;
      const min = parseInt(input.min || "1", 10);
      const max = parseInt(input.max || "999", 10);
      const next = Math.max(min, Math.min(max, (parseInt(input.value, 10) || min) + delta));
      input.value = String(next);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });

  /* -------------------------------------------------------------
   * Scroll-top button (visible after scrolling past 600px)
   * ----------------------------------------------------------- */
  const scrollTopBtn = document.querySelector("[data-scroll-top]");
  if (scrollTopBtn) {
    const onScroll = () => {
      scrollTopBtn.style.display = window.scrollY > 600 ? "" : "none";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    scrollTopBtn.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    onScroll();
  }

  /* -------------------------------------------------------------
   * Sticky buy bar on product pages — visible after scrolling past
   * the main add-to-cart button.
   * ----------------------------------------------------------- */
  const stickyBuy = document.querySelector("[data-sticky-buy]");
  const buyBtn = document.querySelector("[data-product-submit]");
  if (stickyBuy && buyBtn) {
    const onScroll = () => {
      const rect = buyBtn.getBoundingClientRect();
      stickyBuy.classList.toggle("is-visible", rect.bottom < 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* -------------------------------------------------------------
   * Recently-viewed products
   * Stored as a list of handles in localStorage (max 8).
   * On product pages we add the current handle.
   * On any page with [data-recently-viewed], we render cards by
   * fetching `/products/<handle>.json` for each.
   * ----------------------------------------------------------- */
  const RECENT_KEY = "fars_recent";
  const MAX_RECENT = 8;
  function readRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
    catch (e) { return []; }
  }
  function writeRecent(arr) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, MAX_RECENT))); }
    catch (e) {}
  }
  const productMeta = document.querySelector("[data-product-meta]");
  const currentHandle = productMeta ? productMeta.getAttribute("data-product-handle") : null;
  if (currentHandle) {
    const list = readRecent().filter((h) => h !== currentHandle);
    list.unshift(currentHandle);
    writeRecent(list);
  }

  const recentSection = document.querySelector("[data-recently-viewed]");
  const recentGrid = document.querySelector("[data-recently-viewed-grid]");
  if (recentSection && recentGrid) {
    const handles = readRecent().filter((h) => h !== currentHandle).slice(0, 4);
    if (handles.length > 0) {
      Promise.all(handles.map((h) =>
        fetch(`/products/${encodeURIComponent(h)}.js`).then((r) => r.ok ? r.json() : null).catch(() => null)
      )).then((products) => {
        const valid = products.filter(Boolean);
        if (valid.length === 0) return;
        recentSection.style.display = "";
        valid.forEach((p) => {
          const a = document.createElement("a");
          a.className = "product-card";
          a.href = p.url;
          const img = p.featured_image ? `<img src="${p.featured_image}" alt="${p.title}" loading="lazy" class="product-card__img product-card__img--primary">` : `<div class="product-card__placeholder"><span style="font-size:1.5rem;letter-spacing:0.3em;color:rgba(0,0,0,.18);font-weight:300;">FARS</span></div>`;
          const yen = (p.price / 100).toLocaleString("ja-JP");
          a.innerHTML = `
            <div class="product-card__media">${img}</div>
            <p class="product-card__brand">FARS</p>
            <h3 class="product-card__title">${p.title}</h3>
            <span class="product-card__price">¥${yen}<span class="product-card__price-unit">税込</span></span>`;
          recentGrid.appendChild(a);
        });
      });
    }
  }

  /* -------------------------------------------------------------
   * Cart drawer
   * Triggered by [data-cart-drawer-open] (header cart icon).
   * Closed via overlay or [data-cart-drawer-close].
   * Renders cart items by calling /cart.js.
   * ----------------------------------------------------------- */
  const drawer = document.querySelector("[data-cart-drawer]");
  if (drawer) {
    const body = drawer.querySelector("[data-cart-drawer-body]");
    const footer = drawer.querySelector("[data-cart-drawer-footer]");
    const subtotalEl = drawer.querySelector("[data-cart-drawer-subtotal]");
    const cartCountEl = document.querySelector("[data-cart-count]");

    function formatYen(cents) {
      return "¥" + (cents / 100).toLocaleString("ja-JP");
    }

    function render(cart) {
      if (!cart || cart.item_count === 0) {
        body.innerHTML = '<p class="text-muted text-center" style="padding: 3rem 0;">カートは空です。</p>';
        footer.style.display = "none";
      } else {
        body.innerHTML = cart.items.map((item) => `
          <div class="cart-drawer__item">
            <a href="${item.url}" class="cart-drawer__media">
              ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
            </a>
            <div class="cart-drawer__info">
              <p><a href="${item.url}">${item.product_title}</a></p>
              ${item.variant_title && item.variant_title !== "Default Title" ? `<p class="cart-drawer__meta">${item.variant_title}</p>` : ''}
              <p class="cart-drawer__qty">数量: ${item.quantity}</p>
            </div>
            <p class="cart-drawer__price">${formatYen(item.final_line_price)}</p>
          </div>
        `).join("");
        footer.style.display = "";
        if (subtotalEl) subtotalEl.textContent = formatYen(cart.items_subtotal_price);
      }
      if (cartCountEl) {
        cartCountEl.textContent = String(cart.item_count || 0);
        cartCountEl.style.display = cart.item_count > 0 ? "" : "none";
      }
    }

    function open() {
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      fetch("/cart.js").then((r) => r.json()).then(render).catch(() => {});
    }
    function close() {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    }

    document.querySelectorAll("[data-cart-drawer-open]").forEach((el) => {
      el.addEventListener("click", (e) => { e.preventDefault(); open(); });
    });
    drawer.querySelectorAll("[data-cart-drawer-close]").forEach((el) => {
      el.addEventListener("click", close);
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
    });

    // After successful add-to-cart, refresh count & open drawer.
    document.addEventListener("submit", async (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (e.defaultPrevented) return;
      e.preventDefault();
      const data = new FormData(form);
      try {
        const res = await fetch("/cart/add.js", { method: "POST", body: data, headers: { "Accept": "application/json" } });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          const errEl = form.querySelector("[data-product-error]");
          if (errEl) {
            errEl.textContent = errBody.description || "カートに追加できませんでした。";
            errEl.style.display = "";
          }
          return;
        }
        open();
      } catch (err) {
        // Fallback: submit normally
        form.submit();
      }
    });
  }

  /* -------------------------------------------------------------
   * Animation layer v2 — [data-reveal] / [data-reveal-stagger] / hero 視差
   * 見た目はオリジナルのまま、モーションのみ移植（CSS は base.css 末尾）。
   * line バリアント（完全クリップ）は要素自身を観測すると交差面積 0 で
   * 永遠に発火しないため、クリップされない親をトリガーに観測し、
   * クラスは本体へ付与する（trigger/target 分離）。
   * ----------------------------------------------------------- */
  const reduceMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // stagger: 親に data-reveal-stagger="80" → 子 [data-reveal] へ 80ms 刻みの delay
  document.querySelectorAll("[data-reveal-stagger]").forEach((parent) => {
    const step = parseInt(parent.getAttribute("data-reveal-stagger"), 10) || 80;
    parent.querySelectorAll("[data-reveal]").forEach((kid, i) => {
      kid.style.transitionDelay = i * step + "ms";
    });
  });

  const revealEls = document.querySelectorAll("[data-reveal]");
  if (revealEls.length > 0) {
    if (reduceMotion || !("IntersectionObserver" in window)) {
      revealEls.forEach((el) => el.classList.add("is-inview"));
    } else {
      const revealTargets = new Map(); // trigger -> [reveal elements]
      revealEls.forEach((el) => {
        const trigger =
          el.getAttribute("data-reveal") === "line" && el.parentElement
            ? el.parentElement
            : el;
        if (!revealTargets.has(trigger)) revealTargets.set(trigger, []);
        revealTargets.get(trigger).push(el);
      });
      const revealIO = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              (revealTargets.get(entry.target) || [entry.target]).forEach((el) =>
                el.classList.add("is-inview")
              );
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
      );
      revealTargets.forEach((_els, trigger) => revealIO.observe(trigger));
    }
  }

  // ヒーロー背景の視差（rAF + passive。reduced-motion 時は無効）
  const heroBg = document.querySelector("[data-hero-parallax]");
  if (heroBg && !reduceMotion) {
    let heroTicking = false;
    const syncHero = () => {
      heroBg.style.transform = "translate3d(0," + window.scrollY * 0.12 + "px,0)";
      heroTicking = false;
    };
    window.addEventListener(
      "scroll",
      () => {
        if (!heroTicking) {
          heroTicking = true;
          requestAnimationFrame(syncHero);
        }
      },
      { passive: true }
    );
    syncHero();
  }
})();
